/**
 * Teen Patti Hand Evaluator
 *
 * Evaluates 3-card hands, classifies into 6 ranks, generates comparison
 * keys for deterministic ordering, and provides human-readable descriptions.
 *
 * Hand Rankings (highest to lowest):
 *   Trail (Three of a Kind) > Pure Sequence (Straight Flush) >
 *   Sequence (Straight) > Color (Flush) > Pair > High Card
 *
 * Special rules:
 *   - A-2-3 is a valid sequence (second highest after A-K-Q)
 *   - K-A-2 is NOT a valid sequence
 *   - Suit tiebreaker: Spades > Hearts > Diamonds > Clubs
 *     (only when all card ranks are identical)
 */

import { Card, Rank, TeenPattiHandRank, TeenPattiHandResult, RANK_VALUES, SUIT_VALUES } from './types';

// ============================================================================
// Internal helpers
// ============================================================================

function rankValue(rank: Rank): number {
  return RANK_VALUES[rank];
}

function rankName(rank: Rank): string {
  const names: Record<Rank, string> = {
    '2': 'Twos', '3': 'Threes', '4': 'Fours', '5': 'Fives',
    '6': 'Sixes', '7': 'Sevens', '8': 'Eights', '9': 'Nines',
    '10': 'Tens', 'J': 'Jacks', 'Q': 'Queens', 'K': 'Kings', 'A': 'Aces',
  };
  return names[rank];
}

function rankNameSingular(rank: Rank): string {
  const names: Record<Rank, string> = {
    '2': 'Two', '3': 'Three', '4': 'Four', '5': 'Five',
    '6': 'Six', '7': 'Seven', '8': 'Eight', '9': 'Nine',
    '10': 'Ten', 'J': 'Jack', 'Q': 'Queen', 'K': 'King', 'A': 'Ace',
  };
  return names[rank];
}

/**
 * Sort 3 cards by rank descending.
 */
function sortByRankDesc(cards: Card[]): Card[] {
  return [...cards].sort((a, b) => rankValue(b.rank) - rankValue(a.rank));
}

/**
 * Check if all 3 cards have the same rank (Trail).
 */
function isTrail(cards: Card[]): boolean {
  return cards[0].rank === cards[1].rank && cards[1].rank === cards[2].rank;
}

/**
 * Check if all 3 cards have the same suit.
 */
function isSameSuit(cards: Card[]): boolean {
  return cards[0].suit === cards[1].suit && cards[1].suit === cards[2].suit;
}

/**
 * Check if 3 sorted cards form a sequence (consecutive ranks).
 * Returns the sequence type and effective high values for comparison.
 *
 * A-K-Q is highest sequence.
 * A-2-3 is second highest (Ace wraps low).
 * K-A-2 is NOT valid.
 */
function getSequenceInfo(sorted: Card[]): { isSequence: boolean; compValues: number[] } {
  const v0 = rankValue(sorted[0].rank); // highest
  const v1 = rankValue(sorted[1].rank);
  const v2 = rankValue(sorted[2].rank); // lowest

  // Normal consecutive: v0 - v1 = 1 and v1 - v2 = 1
  if (v0 - v1 === 1 && v1 - v2 === 1) {
    return { isSequence: true, compValues: [v0, v1, v2] };
  }

  // A-2-3 special case: sorted = [A(14), 3, 2]
  if (v0 === 14 && v1 === 3 && v2 === 2) {
    // A-2-3 is second highest sequence. For comparison, use [14, 3, 2]
    // This ranks below A-K-Q [14, 13, 12] but above K-Q-J [13, 12, 11]
    return { isSequence: true, compValues: [14, 3, 2] };
  }

  return { isSequence: false, compValues: [] };
}

/**
 * Check for a pair in 3 cards. Returns pair rank and kicker rank.
 */
function getPairInfo(sorted: Card[]): { isPair: boolean; pairRank: number; kicker: number } {
  const v0 = rankValue(sorted[0].rank);
  const v1 = rankValue(sorted[1].rank);
  const v2 = rankValue(sorted[2].rank);

  if (v0 === v1) {
    return { isPair: true, pairRank: v0, kicker: v2 };
  }
  if (v1 === v2) {
    return { isPair: true, pairRank: v1, kicker: v0 };
  }
  return { isPair: false, pairRank: 0, kicker: 0 };
}

/**
 * Get the highest suit value among the cards (for tiebreaking identical ranks).
 */
function highestSuitValue(cards: Card[]): number {
  return Math.max(...cards.map(c => SUIT_VALUES[c.suit]));
}

// ============================================================================
// Public API
// ============================================================================

/**
 * Evaluate a 3-card Teen Patti hand.
 */
export function evaluate(cards: Card[]): TeenPattiHandResult {
  if (cards.length !== 3) {
    throw new Error(`Teen Patti requires exactly 3 cards, got ${cards.length}`);
  }

  const sorted = sortByRankDesc(cards);
  const sameSuit = isSameSuit(sorted);
  const trail = isTrail(sorted);
  const seqInfo = getSequenceInfo(sorted);
  const pairInfo = getPairInfo(sorted);

  const v0 = rankValue(sorted[0].rank);
  const v1 = rankValue(sorted[1].rank);
  const v2 = rankValue(sorted[2].rank);

  let rank: TeenPattiHandRank;
  let comparisonKey: number[];

  if (trail) {
    // Trail (Three of a Kind)
    rank = TeenPattiHandRank.TRAIL;
    comparisonKey = [TeenPattiHandRank.TRAIL, v0];
  } else if (sameSuit && seqInfo.isSequence) {
    // Pure Sequence (Straight Flush)
    rank = TeenPattiHandRank.PURE_SEQUENCE;
    comparisonKey = [TeenPattiHandRank.PURE_SEQUENCE, ...seqInfo.compValues];
  } else if (seqInfo.isSequence) {
    // Sequence (Straight)
    rank = TeenPattiHandRank.SEQUENCE;
    comparisonKey = [TeenPattiHandRank.SEQUENCE, ...seqInfo.compValues];
  } else if (sameSuit) {
    // Color (Flush)
    rank = TeenPattiHandRank.COLOR;
    comparisonKey = [TeenPattiHandRank.COLOR, v0, v1, v2];
  } else if (pairInfo.isPair) {
    // Pair
    rank = TeenPattiHandRank.PAIR;
    comparisonKey = [TeenPattiHandRank.PAIR, pairInfo.pairRank, pairInfo.kicker];
  } else {
    // High Card
    rank = TeenPattiHandRank.HIGH_CARD;
    comparisonKey = [TeenPattiHandRank.HIGH_CARD, v0, v1, v2];
  }

  const result: TeenPattiHandResult = {
    rank,
    rankName: '',
    cards: sorted,
    comparisonKey,
  };

  result.rankName = describeHand(result);
  return result;
}

/**
 * Compare two evaluated hands.
 * Returns 1 if hand1 wins, -1 if hand2 wins, 0 if tie.
 *
 * When comparison keys are equal, uses suit tiebreaker on highest card.
 */
export function compare(hand1: TeenPattiHandResult, hand2: TeenPattiHandResult): number {
  const key1 = hand1.comparisonKey;
  const key2 = hand2.comparisonKey;

  const len = Math.max(key1.length, key2.length);
  for (let i = 0; i < len; i++) {
    const v1 = key1[i] ?? 0;
    const v2 = key2[i] ?? 0;
    if (v1 > v2) return 1;
    if (v1 < v2) return -1;
  }

  // All rank values identical — use suit tiebreaker on highest card
  const suit1 = highestSuitValue(hand1.cards);
  const suit2 = highestSuitValue(hand2.cards);
  if (suit1 > suit2) return 1;
  if (suit1 < suit2) return -1;

  return 0;
}

/**
 * Generate a human-readable description of a hand.
 */
export function describeHand(result: TeenPattiHandResult): string {
  const sorted = result.cards;

  switch (result.rank) {
    case TeenPattiHandRank.TRAIL:
      return `Trail, ${rankName(sorted[0].rank)}`;

    case TeenPattiHandRank.PURE_SEQUENCE: {
      const labels = sorted.map(c => c.rank === '10' ? '10' : c.rank);
      // For A-2-3, display as A-2-3
      const v0 = rankValue(sorted[0].rank);
      const v2 = rankValue(sorted[2].rank);
      if (v0 === 14 && v2 === 2) {
        return `Pure Sequence, A-2-3`;
      }
      return `Pure Sequence, ${labels.join('-')}`;
    }

    case TeenPattiHandRank.SEQUENCE: {
      const v0 = rankValue(sorted[0].rank);
      const v2 = rankValue(sorted[2].rank);
      if (v0 === 14 && v2 === 2) {
        return `Sequence, A-2-3`;
      }
      const labels = sorted.map(c => c.rank === '10' ? '10' : c.rank);
      return `Sequence, ${labels.join('-')}`;
    }

    case TeenPattiHandRank.COLOR:
      return `Color, ${rankNameSingular(sorted[0].rank)} high`;

    case TeenPattiHandRank.PAIR: {
      const pairInfo = getPairInfo(sorted);
      const pairRank = sorted.find(c => rankValue(c.rank) === pairInfo.pairRank)!.rank;
      return `Pair, ${rankName(pairRank)}`;
    }

    case TeenPattiHandRank.HIGH_CARD:
      return `High Card, ${rankNameSingular(sorted[0].rank)}`;

    default:
      return 'Unknown Hand';
  }
}
